/**
 * healthEvents.js — iter760 + iter761 (Feb 2026)
 *
 * Lightweight cross-component event bus with LAST-EVENT REPLAY.
 * When a user adds / removes a structured health condition (via
 * HealthConditionsCard, OnboardingWizard, or ProfileEditModal), we
 * broadcast a `conditions-changed` event that BHI / BTRE / BPP / Twin
 * dashboards subscribe to for live re-fetch — no Redux, no websockets.
 *
 * iter761: the last event `detail` is cached in module-scope + sessionStorage
 * (5-min TTL) so a listener that mounts AFTER the emit still receives the
 * data on subscribe. Required by <ConditionInfluencePrompt> which lives on
 * /health-intelligence/breast — the emit typically happens on /profile.
 *
 * Usage:
 *   import { emitHealthEvent, useHealthEvent } from "@/utils/healthEvents";
 *   emitHealthEvent("conditions-changed", { added: ["pcos"], delta });
 *   useHealthEvent("conditions-changed", (detail) => { ... });   // replay-aware
 */
import { useEffect, useRef } from "react";

const EVT_PREFIX = "nutramilo-health:";
const REPLAY_TTL_MS = 5 * 60 * 1000;       // 5 minutes
const SS_KEY = "nutramilo:lastHealthEvents";

// Module-scope cache (fastest path).
const lastEvents = {};   // { [name]: { detail, ts } }

function _loadSs() {
  try {
    const raw = sessionStorage.getItem(SS_KEY);
    if (!raw) return;
    const obj = JSON.parse(raw);
    if (obj && typeof obj === "object") {
      for (const k of Object.keys(obj)) lastEvents[k] = obj[k];
    }
  } catch { /* ignore */ }
}
function _saveSs() {
  try { sessionStorage.setItem(SS_KEY, JSON.stringify(lastEvents)); } catch { /* ignore */ }
}
_loadSs();

export function emitHealthEvent(name, detail = {}) {
  try {
    lastEvents[name] = { detail, ts: Date.now() };
    _saveSs();
    window.dispatchEvent(new CustomEvent(`${EVT_PREFIX}${name}`, { detail }));
  } catch { /* SSR / non-browser guard */ }
}

/**
 * React hook: subscribes to a health-event and calls `handler(detail)`.
 * If a matching event was emitted within REPLAY_TTL_MS BEFORE the hook
 * mounted, the handler is invoked once with that cached detail. Each
 * cached event is only replayed to a given subscriber ONCE (via a ref).
 * Handler ref is refreshed each render so the subscription itself only
 * rebinds when `name` changes (prevents double-fire loops).
 */
export function useHealthEvent(name, handler) {
  const handlerRef = useRef(handler);
  handlerRef.current = handler;
  const replayedTs = useRef(0);

  useEffect(() => {
    if (typeof window === "undefined") return;
    const evt = `${EVT_PREFIX}${name}`;

    // Replay the most recent cached event if fresh + not already replayed.
    const cached = lastEvents[name];
    if (cached && Date.now() - cached.ts < REPLAY_TTL_MS && cached.ts !== replayedTs.current) {
      replayedTs.current = cached.ts;
      try { handlerRef.current?.(cached.detail || {}); } catch { /* swallow */ }
    }

    const wrapped = (e) => {
      // Mark this ts as consumed so a future re-mount doesn't replay it.
      replayedTs.current = lastEvents[name]?.ts || Date.now();
      try { handlerRef.current?.(e?.detail || {}); } catch { /* swallow */ }
    };
    window.addEventListener(evt, wrapped);
    return () => window.removeEventListener(evt, wrapped);
  }, [name]);
}
